// @services/stubs/StubPaymentClient.ts
import type { PaymentClient } from '@services/clients/PaymentClient';
import type { DemographicInformation, PaymentError, PromoCode, PromoCodeResponse, SubscriptionPlan } from "@ps-bridge/models/core/dataContracts";
import type { TaskEitherTs } from '@ps-bridge/interop';
import * as E from 'fp-ts/lib/Either';

import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import { makePromoCodeStatus } from "@ps-bridge/models/brands/promoCodeStatus";
import { makePromoCode } from "@ps-bridge/models/brands/promoCode";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import { taskEitherFromRaw } from '@utils/effects/branded';
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import { optionToPsMaybe } from "@src/purescript/interop";

const delay = (ms: number): Promise<Ok> =>
    new Promise((resolve) => setTimeout(() => resolve(ok), ms));

const delayedRight = <Error, Value>(ms: number, value: Value): TaskEitherTs<Error, Value> =>
    taskEitherFromRaw(async () => {
        await delay(ms);
        return E.right(value);
    });

const delayedLeft = <Error, Value>(ms: number, error: Error): TaskEitherTs<Error, Value> =>
    taskEitherFromRaw(async () => {
        await delay(ms);
        return E.left(error);
    });

const STUB_PAYMENT_MODE_KEY = "stub:payment:mode";
const STUB_PAYMENT_ERROR_TYPE_KEY = "stub:payment:errorType";
const STUB_PAYMENT_ERROR_MESSAGE_KEY = "stub:payment:errorMessage";
const DEFAULT_PAYMENT_ERROR_TYPE = "NetworkError";
const DEFAULT_PAYMENT_ERROR_MESSAGE = "Unable to continue to payment setup.";

const getStubStorageValue = (key: string): OptionTs<string> => {
    if (typeof window === "undefined") {
        return Option.none();
    }
    try {
        const value = window.localStorage.getItem(key);
        return typeof value === "string" ? Option.some(value) : Option.none();
    } catch {
        return Option.none();
    }
};

const getStubPaymentMode = (): "success" | "error" =>
    Option.fold(
        getStubStorageValue(STUB_PAYMENT_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const buildPaymentError = (): PaymentError => ({
    type: Option.fold(
        getStubStorageValue(STUB_PAYMENT_ERROR_TYPE_KEY),
        () => DEFAULT_PAYMENT_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        Option.fold(
            getStubStorageValue(STUB_PAYMENT_ERROR_MESSAGE_KEY),
            () => DEFAULT_PAYMENT_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: optionToPsMaybe(Option.none()),
});

export class StubPaymentClient implements PaymentClient {
    getPublishableKey: TaskEitherTs<PaymentError, string> = taskEitherFromRaw(
        async () => {
        console.log('StubPaymentClient.getPublishableKey called');

            await delay(100);
            return E.right('pk_test_stubbed_key_123');
        },
    );

    setupIntent(
        subscriptionPlan: SubscriptionPlan
    ): TaskEitherTs<PaymentError, string> {
        console.log('StubPaymentClient.setupIntent called with subscription plan:', subscriptionPlan);

        if (getStubPaymentMode() === "error") {
            return delayedLeft(100, buildPaymentError());
        }

        return delayedRight(100, 'pi_test_stubbed_intent_secret_123');
    }

    cancelSubscription: TaskEitherTs<PaymentError, Ok> = delayedRight(100, ok);

    // New method: setPromoCode
    validatePromoCode(promoCode: PromoCode): TaskEitherTs<PaymentError, PromoCodeResponse> {
        console.log('StubPaymentClient.setPromoCode called with promo code:', promoCode);

        // Simulate delay and return a successful PromoCodeResponse
        const isValidCode = promoCode === makePromoCode('VALID_PROMO');
        const response: PromoCodeResponse = {
            promo_status: isValidCode ? makePromoCodeStatus('Valid') : makePromoCodeStatus('Not Found'),
        };

        return delayedRight(100, response);
    }

    // Updated method: submitDemographicInformation
    submitDemographicInformation(
        promoCode: PromoCode,
        demographicInfo: DemographicInformation
    ): TaskEitherTs<PaymentError, Ok> {
        console.log('StubPaymentClient.submitDemographicInformation called with promo code:', promoCode);
        console.log('StubPaymentClient.submitDemographicInformation called with demographic info:', demographicInfo);

        if (getStubPaymentMode() === "error") {
            return delayedLeft(100, buildPaymentError());
        }

        return delayedRight(100, ok);
    }

    submitSquareCardNonce(nonce: string): TaskEitherTs<PaymentError, Ok> {
        console.log('StubPaymentClient.submitSquareCardNonce called with nonce:', nonce);
        return delayedRight(100, ok);
    }
}
